// Copyright (c) 2024 Readymadeui
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import LinkInput from './LinkInput';
import TextUserPair from './TextUserPair';
import CopyTextButton from './CopyTextButton';
import DotSpinner from './DotSpinner';
import Tabs from './Tabs';
import ArtPage from './ArtPage';
import getIconForLink from '../iconForLink.js';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import ImageScroller from './ImageScroller';
import ArtTabs from './ArtTabs';
import 'delayed-scroll-restoration-polyfill';
import ArtThumbnail from './ArtThumbnail.js';
import { formatYear } from './../utils.js'; // Import the global function

// Move cache outside the component to persist across renders
const cache = new Map();

const AddEvent = ({repeatedEnter = false}) => {
  const myUser = useContext(MyUserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [art, setArt] = useState(null);
  const [addedEvents, setAddedEvents] = useState([]);
  // const [successSign, setSuccessSign] = useState(null); // like, todo, finished

  const [searching, setSearching] = useState(false);
  //const [foundArts, setFoundArts] = useState([]);

  // const [foundArtsByType, setFoundArtsByType] = useState(new Map());

  const [foundMovies, setFoundMovies] = useState(null);
  const [foundBooks, setFoundBooks] = useState(null);
  const [foundGames, setFoundGames] = useState(null);

  const searchInputRef = useRef(null);
  const [renderCount, setRenderCount] = useState(0);
  useEffect(() => {
    // alert("repeatedEnter=" + repeatedEnter);
    // Focus the input when the component mounts or when the user navigates back to this page
    if (searchInputRef.current && repeatedEnter) {
      searchInputRef.current.focus();
    }
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const searchTerm = queryParams.get('term');
    const limit = queryParams.get('limit') || 20;
    // const author = queryParams.get('author');
    if (searchTerm) {
    // if (searchTerm || author) {
      // if (author) {
      //   searchTerm = "author:"+author;
      // }
      handleSearchArt(searchTerm, "movie", limit);
      handleSearchArt(searchTerm, "book", limit);
      handleSearchArt(searchTerm, "videoGame", limit);
      searchInputRef.current.value = searchTerm;
    }
  }, []);

  useEffect(() => {
    // Enable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const [enterPressed, setEnterPressed] = useState(false); // to prevent suggest flickering when response for old request comes

  const typingSearchClearTimeoutRef = useRef(null);
  const [searchSuggest, setSearchSuggest] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const userInitiatedFocusRef = useRef(false);
  const handleSearchSuggest = (term) => {
    axiosInstance
    .get(`/arts/searchSuggest?term=${term}`)
    .then((response) => {
      // console.log("term: " + term + ", searchInputRef.current.value: " + searchInputRef.current.value);
      if (searchInputRef.current.value !== term) { // late response // TODO: move into the component's main code
        return;
      }
      setSearchSuggest(response.data);
    })
    .catch((error) => {
      console.log(error);
    })
  };
  const handleSearchHistory = () => {
    axiosInstance
    .get(`/arts/searchHistory`)
    .then((response) => {
      setSearchHistory(response.data || []);
    })
    .catch((error) => {
      console.log(error);
    })
  };

  const handleSearchArt = (term, artType, limit=25) => {
    if (term === "") {
      return;
    }

    const cacheKey = `${term}-${artType}`;
    const cachedResult = cache.get(cacheKey);

    // Remove stale data from cache
    for (const [key, value] of cache.entries()) {
      if (Date.now() - value.timestamp >= 10 * 60 * 1000) { // 10 minutes
        cache.delete(key);
      }
    }

    if (cachedResult && Date.now() - cachedResult.timestamp < 10 * 60 * 1000) {
      const data = cachedResult.data;
      if (artType === "movie") {
        setFoundMovies(data);
      } else if (artType === "book") {
        setFoundBooks(data);
      } else {
        setFoundGames(data);
      }
      return;
    }

    setSearching(true);
    setFoundMovies(null);
    setFoundGames(null);
    setFoundBooks(null);
    setArt(null);


    axiosInstance
      .get(`/arts/search?term=${term}&artType=${artType}&limit=${limit}`)
      .then((response) => {
        setSearching(false);
        setSearchSuggest(null);

        if (addedEvents.length > 0) {
          return;
        }

        const data = response.data;
        cache.set(cacheKey, { data, timestamp: Date.now() });

        if (artType === "movie") {
          setFoundMovies(data);
        } else if (artType === "book") {
          setFoundBooks(data);
        } else {
          setFoundGames(data);
        }
      })
      .catch((error) => {
        console.error(error);
        setSearching(false);
        if (error.response.status === 400) {
          alert(error.response.data);
        }
      });
  };

  const isMobileDeviceExceptAndroid = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iphone|ipad|ipod|windows phone|blackberry/i.test(userAgent);
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
  };

  const handleArtSubmit = (link) => {
    axiosInstance
    .post(`/arts`, { wikiArticleLink: link })
    .then((response) => {
      // setAddedEvents(prevObjects => [response.data, ...prevObjects]); // not sure
      // setFoundMovies(null);
      // setFoundGames(null);
      // setFoundBooks(null);
      // setActiveTab('movie');
      //setArt(response.data);
      navigate("/arts/"+response.data.id);
      // setLinkInput(""); // not sure
      // setSuccessSign(eventType);
    })
    .catch((error) => {
      console.error(error);
      // setSuccessSign(null);
      // setLinkInputErr(true);
    });
  };

  // Determine the tab to show based on found arts.
  // Expects non-null results and exactly one tab with results.
  const determineTabBasedOnResults = () => {
    let res;
    if (foundMovies.length > 0) {
      res = 'movie';
    } else if (foundBooks.length > 0) {
      res = 'book';
    } else if (foundGames.length > 0) {
      res = 'videoGame';
    }
    return res;
  };

  const [activeTab, setActiveTab] = useState('movie');

  // Update activeTab dynamically when arts are fetched
  useEffect(() => {
    if (!foundMovies || !foundBooks || !foundGames) { // too early to determine tab
      return;
    }
    let nonZeroTabs = 0;
    if (foundMovies.length > 0) nonZeroTabs++;
    if (foundBooks.length > 0) nonZeroTabs++;
    if (foundGames.length > 0) nonZeroTabs++;
    if (nonZeroTabs == 1) {
      setActiveTab(determineTabBasedOnResults());
    }
  }, [foundMovies, foundBooks, foundGames]);

  const foundArtContainerByTab = (tab) => {
    if (tab === "movie") {
      return foundMovies;
    } else if (tab === "book") {
      return foundBooks;
    } else if (tab === "videoGame") {
      return foundGames;
    } else {
      alert("Unexpected tab");
      return foundMovies;
    }
  }

  const eventTypeStr = (i) => {
    const m = {
      0: "none",
      1: "finished",
      2: "todo",
      3: "like"
    };
    return m[i];
  }

  const [recoms, setRecoms] = useState(null);
  const [recomsAIReasonedIDs, setRecomsAIReasonedIDs] = useState(null);
  const [recomsBasedOnRecentLogbookIDs, setRecomsBasedOnRecentLogbookIDs] = useState(null);
  const [recomsV2, setRecomsV2] = useState(null);
  const [recomsUpdatedTime, setRecomsUpdatedTime] = useState(null);
  const [recomsV2UpdatedTime, setRecomsV2UpdatedTime] = useState(null);
  // const [recomVersion, setRecomVersion] = useState(null);

  const fetchRecoms = (yearParamStr, versionParamStr) => {
    axiosInstance
    .get(`/platformRecommendations?${versionParamStr ? "version="+versionParamStr : ""}`+`${yearParamStr ? "year="+yearParamStr : ""}`)
    .then((response) => {
      if (versionParamStr === "2") {
        setRecomsV2(response.data.arts);
        setRecomsV2UpdatedTime(response.data.updatedTime);
      } else {
        setRecoms(response.data.arts);
        setRecomsAIReasonedIDs(response.data.aiReasonedArts || []);
        setRecomsBasedOnRecentLogbookIDs(response.data.basedOnRecentLogbookArts || []);
        setRecomsUpdatedTime(response.data.updatedTime);
      }
    })
    .catch((error) => {
      console.log(error);
    })
  };

  // const [showTrending, setShowTrending] = useState(true); // if false, recommendations are shown
  const [trendingMovies, setTrendingMovies] = useState(null);
  const [trendingMoviesUpdatedTime, setTrendingMoviesUpdatedTime] = useState(null);
  const fetchTrendingMovies = () => {
    const queryParams = new URLSearchParams(location.search);
    const popMostRecent = queryParams.get('popMostRecent');

    return axiosInstance
      .get(`/trendingMovies${popMostRecent ? `?popMostRecent=${popMostRecent}` : ''}`)
      .then((response) => {
        setTrendingMovies(response.data.arts);
        setTrendingMoviesUpdatedTime(response.data.updatedTime);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    const yearParamStr = new URLSearchParams(location.search).get('year');
    (async () => {
      await fetchTrendingMovies();
      handleSearchHistory();
      fetchRecoms(yearParamStr, "");
      fetchRecoms(yearParamStr, "2");
    })();
  }, [location.search]);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const showSuggestList = !enterPressed && searchSuggest && searchSuggest.length > 0;
  const showHistoryList = showSearchHistory && (!searchTerm || searchTerm.length === 0) && searchHistory && searchHistory.length > 0;
  const categoryLabels = {
    movie: language === "ru" ? "Фильмы" : "Movies",
    book: language === "ru" ? "Книги" : "Books",
    videoGame: language === "ru" ? "Игры" : "Video Games"
  };
  const categoryOrder = ["movie", "book", "videoGame"];
  const categoryEnum = {
    movie: 1,
    videoGame: 2,
    book: 3
  };
  const filterByCategory = (arts, category) => {
    if (!arts) {
      return [];
    }
    return arts.filter((item) => item.category === categoryEnum[category]);
  };

  const performSearch = (term) => {
    const nextTerm = term.trim();
    if (nextTerm === "") {
      return;
    }

    setEnterPressed(true);
    setSearchSuggest(null);
    setShowSearchHistory(false);
    setSearchTerm(nextTerm);
    if (searchInputRef.current) {
      searchInputRef.current.value = nextTerm;
    }

    if (nextTerm.startsWith("https://")) {
      handleArtSubmit(nextTerm);
      return;
    }

    handleSearchArt(nextTerm, "movie");
    handleSearchArt(nextTerm, "book");
    handleSearchArt(nextTerm, "videoGame");

    const url = "https://wowcontent.lol";
    //const url = "http://localhost:8080";
    window.history.pushState({}, '', `${url}/addEvent?term=${nextTerm}&tab=${activeTab}`);
  };

  return (
    <div class="container mx-auto w-full lg:w-1/2 px-1 md:px-2 z-1">
      <div>
        <div class="flex mb-2 mt-4 px-4 py-2 rounded-xl border-2 border-gray-300 overflow-hidden max-w-md mx-auto font-[sans-serif]">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.904 192.904" width="16px"
            class="fill-gray-600 mr-3 rotate-120">
            <path
              d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z">
            </path>
          </svg>
          <input ref={searchInputRef} type="search" class="w-full outline-none bg-transparent text-gray-300 text-lg"
            placeholder={language === "ru" ? "Поиск по фильмам, книгам и играм..." : "Search movies, books, or games..."}
            value={searchTerm}
            onKeyDown={
              (e) => {
                // alert("enterPressed became true in onKeyDown");
                setSearchSuggest(null); // TODO: remove?
                setShowSearchHistory(false);
                if (e.key === 'Enter') {
                  performSearch(e.target.value);
                }
              }
            }
            onChange={
              (e) => {
                setEnterPressed(false);
                setShowSearchHistory(false);
                // alert("enterPressed became false");
                setSearchTerm(e.target.value);
                clearTimeout(typingSearchClearTimeoutRef.current);
                typingSearchClearTimeoutRef.current = setTimeout(()=>{
                  handleSearchSuggest(e.target.value);
                }, 90);
              }
            }
            onMouseDown={() => {
              userInitiatedFocusRef.current = true;
            }}
            onTouchStart={() => {
              userInitiatedFocusRef.current = true;
            }}
            onPointerDown={() => {
              userInitiatedFocusRef.current = true;
            }}
            onFocus={() => {
              if (userInitiatedFocusRef.current) {
                setShowSearchHistory(true);
              }
              userInitiatedFocusRef.current = false;
            }}
            onBlur={() => {
              setTimeout(() => {
                setShowSearchHistory(false);
              }, 150);
            }}
            autofocus
          />
          {
          (isMobileDeviceExceptAndroid() || isFirefox()) && searchTerm &&
            <span class="hover:cursor-pointer text-lg" onClick={()=>{
                setSearchTerm(''); // really needed here if ref is updated too?
                searchInputRef.current.value = '';
                searchInputRef.current.focus();
              }
            }>
              &times;
            </span>
          }
        </div>
        <div class="flex justify-center">
          <div class="absolute z-50 bg-[#3a3b3c] rounded-md max-w-md mx-auto w-full">
            {showSuggestList && searchSuggest.map((suggest, index) => (
              <div
                key={suggest.imageUrl}
                className={"p-2 cursor-pointer flex items-center bg-[#3a3b3c] hover:bg-[#2e2f30]" + (index==0 ? " rounded-t-md" : "") + (index == searchSuggest.length-1 ? " rounded-b-md" : "")}
                onClick={() => {
                  performSearch(suggest.text);
                }}
              >
                {/*<img src={suggest.imageUrl} class="max-w-8 max-h-8" /> &nbsp;*/}
                {suggest.text}
              </div>
            ))}
            {showHistoryList && searchHistory.map((term, index) => (
              <div
                key={`${term}-${index}`}
                className={"p-2 cursor-pointer flex items-center bg-[#3a3b3c] hover:bg-[#2e2f30]" + (index==0 ? " rounded-t-md" : "") + (index == searchHistory.length-1 ? " rounded-b-md" : "")}
                onClick={() => {
                  performSearch(term);
                }}
              >
                {term}
              </div>
            ))}
          </div>
        </div>
      </div>      

      {
      (foundMovies || foundBooks || foundGames) &&
      <div class="my-2">
        <ArtTabs 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          language={language} 
          movies={foundMovies} 
          books={foundBooks} 
          games={foundGames} 
                  />
      </div>
      }

      {
        searching && 
        <div class="flex justify-center"> 
          <DotSpinner />
        </div>
      }

      <div class="flex justify-center">
        <div id={activeTab} className="grid grid-cols-4 gap-3">
          {foundArtContainerByTab(activeTab) && foundArtContainerByTab(activeTab).map((foundArt, index) => (
            <div id={foundArt.pageUrl} class="hover:shadow-lg transition-shadow duration-200">
              <div>
                <div id={foundArt.pageUrl+"0"} class="flex justify-center"> 
                  <Link to={"/artByLink?link="+foundArt.pageUrl}>
                    <ArtThumbnail art={foundArt.sourceArt ? foundArt.sourceArt : {thumbnailUrl: foundArt.thumbUrl}} />
                  </Link>
                </div>
                <div class="flex justify-center items-center text-center w-full h-auto text-sm font-medium mt-2"> 
                  {( (language === "en" || !foundArt.ruName) ? foundArt.enName : foundArt.ruName) + formatYear(foundArt.year, language)}
                </div>
                {activeTab === "book" && foundArt.author && (
                  <div class="flex justify-center items-center text-center w-full h-auto text-xs text-gray-400 mt-1">
                    {foundArt.author}
                  </div>
                )}
                <div class="flex justify-center mt-1">
                {
                  foundArt.sourceArt ?
                    <PlatformRatings art={foundArt.sourceArt} />
                  :
                    foundArt.ratings &&
                      <PlatformRatings art={foundArt.ratings} />
                }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {
      (trendingMovies && !(foundMovies || foundBooks || foundGames) && !searchTerm) &&
      <>
        {categoryOrder.map((category) => {
          const filtered = filterByCategory(trendingMovies, category);
          if (filtered.length === 0) {
            return null;
          }
          return (
            <div key={`trending-${category}`} class="mt-4">
              <div class="flex justify-center font-bold text-lg">
                {language === "ru" ? `Свежее - ${categoryLabels[category]}` : `Trending - ${categoryLabels[category]}`}
              </div>
              <div class="mt-2 flex justify-center text-xs text-gray-500 text-center">
                {language === "ru" ? "Обновлено " : "Updated "} {new Date(trendingMoviesUpdatedTime).toLocaleDateString(language, { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })}
              </div>
              <div class="mt-2">
                <ImageScroller
                  arts={filtered}
                  categories={[category]}
                  showCategoryTitles={false}
                  aiReasonedArts={recomsAIReasonedIDs || []}
                  basedOnRecentLogbookArts={recomsBasedOnRecentLogbookIDs || []}
                />
              </div>
            </div>
          );
        })}
      </>
      }

      {
      (myUser && recoms && !(foundMovies || foundBooks || foundGames) && !searchTerm) &&
      <>
        {/* <div class="mt-3 flex justify-center">
          {language === "ru" ? "Более надежные" : "More reliable"}
        </div> */}
        {categoryOrder.map((category) => {
          const filtered = filterByCategory(recoms, category);
          if (filtered.length === 0) {
            return null;
          }
          return (
            <div key={`recoms-${category}`} class="mt-4">
              <div class="flex justify-center font-bold text-lg">
                {language === "ru" ? `Рекомендации от ИИ - ${categoryLabels[category]}` : `Recommendations from AI - ${categoryLabels[category]}`}
              </div>
              <div class="mt-2 flex justify-center text-xs text-gray-500 text-center">
                {language === "ru" ? "Обновлено " : "Updated "} {new Date(recomsUpdatedTime).toLocaleDateString(language, { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })}
              </div>
              <div class="mt-2">
                <ImageScroller
                  arts={filtered}
                  categories={[category]}
                  showCategoryTitles={false}
                  aiReasonedArts={recomsAIReasonedIDs || []}
                  basedOnRecentLogbookArts={recomsBasedOnRecentLogbookIDs || []}
                />
              </div>
            </div>
          );
        })}
      </> 
      }

    </div>
  );
}

export default AddEvent;
